import { useState, useEffect, useRef } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { cn } from '../../../lib/utils';
import {
  FileUp, FileText, ArrowRight, CheckCircle2, AlertCircle,
  Loader2, X, Minimize2, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { useBackgroundJobs } from '../../../contexts/BackgroundJobsContext';
import { useAppMode } from '../../../contexts/AppModeContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ── Target field definitions for column mapping ──
const TARGET_FIELDS = [
  { value: 'first_name', label: 'First Name', required: true },
  { value: 'last_name', label: 'Last Name', required: true },
  { value: 'date_of_birth', label: 'Date of Birth', required: true },
  { value: 'middle_name', label: 'Middle Name' },
  { value: 'gender', label: 'Gender' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'street', label: 'Street' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zip_code', label: 'ZIP Code' },
  { value: 'payer_name', label: 'Payer Name' },
  { value: 'member_id', label: 'Member ID' },
  { value: 'group_number', label: 'Group Number' },
];
const REQUIRED_TARGETS = TARGET_FIELDS.filter(f => f.required).map(f => f.value);
const USE_VENDOR_FLOW = process.env.REACT_APP_USE_VENDOR_MAPPING_FLOW !== 'false';
const COMPOSITE_PATTERNS = [
  { value: 'LastName, FirstName', label: 'Last, First (e.g. "Smith, John")' },
  { value: 'FirstName LastName', label: 'First Last (e.g. "John Smith")' },
];

// ── Column type heuristics ──
const _DATE_RE = /^(\d{4}[-/]\d{1,2}[-/]\d{1,2}|\d{1,2}[-/]\d{1,2}[-/]\d{2,4})$/;
const _PHONE_RE = /^[\d()+\-.\s]{7,20}$/;
const _EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const _FULLNAME_RE = /^[A-Za-z'-]+[,\s]+[A-Za-z'-]+/;

function _guessColumnType(values) {
  const samples = values.filter(Boolean).slice(0, 10);
  if (samples.length === 0) return null;
  const matchRate = (re) => samples.filter(v => re.test(v.trim())).length / samples.length;
  if (matchRate(_EMAIL_RE) > 0.5) return { type: 'email', label: 'Looks like Email' };
  if (matchRate(_DATE_RE) > 0.6) return { type: 'date', label: 'Looks like Date' };
  if (matchRate(_PHONE_RE) > 0.5) return { type: 'phone', label: 'Looks like Phone' };
  if (matchRate(_FULLNAME_RE) > 0.5) return { type: 'fullname', label: 'Looks like Full Name' };
  return null;
}

function _getSamples(rows, header, count = 2) {
  const vals = [];
  for (const row of (rows || [])) {
    const v = (row[header] || '').trim();
    if (v && !vals.includes(v)) vals.push(v);
    if (vals.length >= count) break;
  }
  return vals;
}

const HINT_COLORS = {
  date: 'text-violet-600 bg-violet-50 border-violet-200',
  phone: 'text-sky-600 bg-sky-50 border-sky-200',
  email: 'text-teal-600 bg-teal-50 border-teal-200',
  fullname: 'text-amber-600 bg-amber-50 border-amber-200',
};

const UploadPatientsModal = ({ open, onOpenChange, onSuccess, minimizedJobId, onMinimize, onClearMinimized, initialFile }) => {
  const { isDemo } = useAppMode();
  const { authFetch } = useAuth();
  const { addJob } = useBackgroundJobs();
  const fileRef = useRef(null);
  const [step, setStep] = useState('pick');
  const [file, setFile] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const initialFileConsumed = useRef(false);

  const [columnMapping, setColumnMapping] = useState({});
  const [mappingErrors, setMappingErrors] = useState([]);

  const [vendorFields, setVendorFields] = useState(null);
  const [compositeSplit, setCompositeSplit] = useState(null);

  const pollRef = useRef(null);

  const effectiveFields = vendorFields || TARGET_FIELDS;
  const effectiveRequired = effectiveFields.filter(f => f.required).map(f => f.value);

  useEffect(() => {
    if (!open) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (minimizedJobId) {
      setCurrentJobId(minimizedJobId);
      setStep('importing');
      setBusy(true);
      _startPolling(minimizedJobId);
      onClearMinimized?.();
    } else {
      setStep('pick'); setFile(null); setParseResult(null); setImportResult(null); setColumnMapping({}); setMappingErrors([]);
      setCurrentJobId(null); setVendorFields(null); setCompositeSplit(null);
      initialFileConsumed.current = false;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && initialFile && !minimizedJobId && !initialFileConsumed.current) {
      initialFileConsumed.current = true;
      parseFileAndProceed(initialFile);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFile]);

  const normalizeHeaders = (data) => {
    let headers = data.source_headers;
    if (Array.isArray(headers) && headers.length === 1 && typeof headers[0] === 'string' && headers[0].includes(',')) {
      headers = headers[0].split(',');
    }
    if (typeof headers === 'string') {
      headers = headers.split(',');
    }
    if (!Array.isArray(headers)) headers = [];
    const seen = new Set();
    headers = headers.map(h => (typeof h === 'string' ? h.trim() : String(h))).filter(h => {
      if (!h || seen.has(h)) return false;
      seen.add(h);
      return true;
    });

    if (headers.length <= 1) {
      const internalKeys = new Set(['_row']);
      const sourceRows = data.raw_rows || data.rows || [];
      if (sourceRows.length > 0) {
        const derived = Object.keys(sourceRows[0]).filter(k => !internalKeys.has(k));
        if (derived.length > 1) headers = derived;
      }
      if (headers.length <= 1 && data.errors?.length > 0 && data.errors[0]?.data) {
        const errKeys = Object.keys(data.errors[0].data);
        if (errKeys.length > 1) headers = errKeys;
      }
    }

    return headers;
  };

  const normalizeAutoMapping = (origMapping, normalizedHeaders) => {
    if (!origMapping || typeof origMapping !== 'object') return {};
    const headerSet = new Set(normalizedHeaders);
    const result = {};
    for (const [src, tgt] of Object.entries(origMapping)) {
      if (headerSet.has(src)) result[src] = tgt;
    }
    return result;
  };

  const parseFileAndProceed = async (f) => {
    setFile(f);
    setStep('parsing');
    setBusy(true);
    setCompositeSplit(null);
    try {
      const fd = new FormData();
      fd.append('file', f);

      const parsePromise = authFetch(`${API}/api/patients/parse-upload`, { method: 'POST', body: fd });
      const schemaPromise = USE_VENDOR_FLOW
        ? authFetch(`${API}/api/patients/field-schema`).catch(() => null)
        : Promise.resolve(null);

      const [res, schemaRes] = await Promise.all([parsePromise, schemaPromise]);

      if (schemaRes?.ok) {
        try {
          const schema = await schemaRes.json();
          if (schema.fields?.length > 0) {
            setVendorFields(schema.fields.map(sf => ({
              value: sf.key || sf.value || sf.name,
              label: sf.label || sf.key || sf.name,
              required: !!sf.required,
            })));
          }
        } catch { /* keep local fallback */ }
      }

      if (res.ok) {
        const data = await res.json();
        const headers = normalizeHeaders(data);
        const autoMap = normalizeAutoMapping(data.auto_mapping, headers);
        const batchSession = crypto.randomUUID ? crypto.randomUUID() : `bs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        setParseResult({ ...data, source_headers: headers, auto_mapping: autoMap, batch_session: batchSession });
        setColumnMapping(autoMap);
        setMappingErrors([]);
        setStep('mapping');
      } else {
        const err = await res.json().catch(() => ({}));
        const status = res.status;
        if (status === 401 || status === 403) toast.error('Authentication required — please log in again');
        else if (status === 422) toast.error(err.detail || 'Invalid file format');
        else if (status === 429) toast.error('Rate limited — please wait and retry');
        else toast.error(err.detail || `Failed to parse file (${status})`);
        setStep('pick');
      }
    } catch {
      toast.error('Network error parsing file');
      setStep('pick');
    }
    setBusy(false);
  };

  const handleFilePick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['csv', 'xls', 'xlsx', 'txt'].includes(ext)) {
      toast.error('Unsupported file type. Use CSV, XLS, XLSX, or TXT.');
      return;
    }
    parseFileAndProceed(f);
  };

  const handleMappingChange = (sourceHeader, targetField) => {
    setColumnMapping(prev => {
      const next = { ...prev };
      if (targetField === '' || targetField === 'skip') {
        delete next[sourceHeader];
      } else {
        next[sourceHeader] = targetField;
      }
      return next;
    });
    if (mappingErrors.length > 0) setMappingErrors([]);
  };

  const handleAutoMap = () => {
    setColumnMapping(parseResult?.auto_mapping || {});
    setMappingErrors([]);
  };

  const handleResetMapping = () => {
    setColumnMapping({});
    setMappingErrors([]);
  };

  const getUsedTargets = () => {
    return new Set(Object.values(columnMapping));
  };

  const validateMapping = () => {
    const errs = [];
    const usedTargets = Object.values(columnMapping);
    for (const req of effectiveRequired) {
      if (compositeSplit && (req === 'first_name' || req === 'last_name')) continue;
      if (!usedTargets.includes(req)) {
        const label = effectiveFields.find(f => f.value === req)?.label || req;
        const candidates = (parseResult?.source_headers || []).filter(h => {
          const samples = _getSamples(parseResult?.raw_rows, h);
          if (samples.length === 0) return false;
          const hint = _guessColumnType((parseResult?.raw_rows || []).map(r => r[h] || ''));
          if (req === 'date_of_birth' && hint?.type === 'date') return true;
          if (req === 'email' && hint?.type === 'email') return true;
          return false;
        });
        const sample = candidates.length > 0 ? _getSamples(parseResult?.raw_rows, candidates[0])[0] : null;
        const hint = sample ? ` — "${candidates[0]}" (e.g. "${sample}") may match` : '';
        errs.push(`"${label}" is required but not mapped${hint}`);
      }
    }
    const seen = new Set();
    for (const [, tgt] of Object.entries(columnMapping)) {
      if (seen.has(tgt)) {
        const label = effectiveFields.find(f => f.value === tgt)?.label || tgt;
        errs.push(`"${label}" is mapped to multiple columns.`);
      }
      seen.add(tgt);
    }
    setMappingErrors(errs);
    return errs.length === 0;
  };

  const applyMappingAndProceed = async () => {
    if (!validateMapping()) return;
    if (!parseResult?.raw_rows) return;

    if (USE_VENDOR_FLOW) {
      setStep('applying');
      setBusy(true);
      try {
        const payload = {
          rows: parseResult.raw_rows,
          column_mapping: columnMapping,
        };
        if (compositeSplit?.source && compositeSplit?.pattern) {
          payload.composite_split = compositeSplit;
        }
        const res = await authFetch(`${API}/api/patients/apply-mapping`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        if (res.ok) {
          const data = await res.json();
          setParseResult(prev => ({
            ...prev,
            rows: data.mapped_rows || [],
            errors: [],
            valid_rows: data.total_mapped || 0,
            error_rows: data.total_skipped || 0,
            total_rows: (data.total_mapped || 0) + (data.total_skipped || 0),
          }));
          if (data.total_skipped > 0) {
            toast.info(`${data.total_skipped} row${data.total_skipped > 1 ? 's' : ''} skipped (missing required fields)`);
          }
          setStep('preview');
        } else {
          const err = await res.json().catch(() => ({}));
          const status = res.status;
          if (status === 422) toast.error(err.detail || 'Mapping validation failed');
          else if (status === 401 || status === 403) toast.error('Authentication required');
          else if (status === 429) toast.error('Rate limited — please wait and retry');
          else toast.error(err.detail || `Apply mapping failed (${status})`);
          setStep('mapping');
        }
      } catch {
        toast.error('Network error applying mapping');
        setStep('mapping');
      }
      setBusy(false);
    } else {
      const validFields = new Set(effectiveFields.map(f => f.value));
      const requiredFields = new Set(effectiveRequired);
      const mappedRows = [];
      const mappedErrors = [];

      for (const rawRow of parseResult.raw_rows) {
        const row = {};
        for (const [srcHeader, tgtField] of Object.entries(columnMapping)) {
          const val = (rawRow[srcHeader] || '').trim();
          if (val && validFields.has(tgtField)) {
            row[tgtField] = val;
          }
        }

        if (compositeSplit?.source && compositeSplit?.pattern) {
          const composite = (rawRow[compositeSplit.source] || '').trim();
          if (composite) {
            if (compositeSplit.pattern === 'FirstName LastName') {
              const parts = composite.split(/\s+/, 2);
              row.first_name = parts[0] || '';
              row.last_name = parts[1] || '';
            } else {
              const parts = composite.split(',', 2).map(s => s.trim());
              row.last_name = parts[0] || '';
              row.first_name = parts[1] || '';
            }
          }
        }

        const missing = [...requiredFields].filter(rf => !row[rf]);
        if (missing.length > 0) {
          mappedErrors.push({ row: rawRow._row || 0, error: `Missing required: ${missing.join(', ')}`, data: row });
        } else {
          row._row = rawRow._row || 0;
          mappedRows.push(row);
        }
      }

      setParseResult(prev => ({
        ...prev,
        rows: mappedRows,
        errors: mappedErrors,
        valid_rows: mappedRows.length,
        error_rows: mappedErrors.length,
        total_rows: mappedRows.length + mappedErrors.length,
      }));
      setStep('preview');
    }
  };

  const _startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);

    const poll = setInterval(async () => {
      try {
        const pollRes = await authFetch(`${API}/api/patients/bulk-import/jobs/${jobId}`);
        let job = null;
        try { job = await pollRes.json(); } catch { return; }
        if (!job || !job.status) return;

        setImportResult(job);

        if (job.status === 'completed') {
          clearInterval(poll);
          pollRef.current = null;
          setStep('done');
          setBusy(false);
          onSuccess?.();
          if (job.imported > 0 && job.errors === 0) {
            toast.success(`Imported ${job.imported} patient${job.imported > 1 ? 's' : ''}`);
          } else if (job.imported === 0 && job.duplicates > 0 && job.errors === 0) {
            toast.info(`All ${job.duplicates} record${job.duplicates > 1 ? 's' : ''} already exist`);
          } else if (job.errors > 0) {
            toast.warning(`Import done: ${job.imported} imported, ${job.duplicates} duplicates, ${job.errors} errors`);
          }
        } else if (job.status === 'failed') {
          clearInterval(poll);
          pollRef.current = null;
          setImportResult({ ...job, _failed: true, _reason: job.error_detail || 'Import job failed', _status: 0 });
          setStep('import_failed');
          setBusy(false);
          toast.error(job.error_detail || 'Import job failed');
        }
      } catch { /* keep trying */ }
    }, 1500);

    pollRef.current = poll;
  };

  const handleImport = async () => {
    if (!parseResult) return;
    setStep('importing');
    setBusy(true);
    setImportResult(null);

    try {
      const batchSession = parseResult.batch_session || `bs-${Date.now()}`;
      const batchName = file?.name || 'upload';
      let importUrl = `${API}/api/patients/bulk-import/start`;

      if (USE_VENDOR_FLOW) {
        const params = new URLSearchParams({ batch_name: batchName, batch_session: batchSession });
        importUrl = `${API}/api/patients/bulk-import/start?${params}`;
      }

      const startRes = await authFetch(importUrl, {
        method: 'POST',
        body: JSON.stringify({ batch_session: batchSession, rows: parseResult.rows }),
      });

      let startData = null;
      try { startData = await startRes.json(); } catch {}

      if (!startData?.job_id) {
        const reason = startData?.detail || `Failed to start import (${startRes.status})`;
        setImportResult({ _failed: true, _reason: reason, _status: startRes.status });
        setStep('import_failed');
        toast.error(reason);
        setBusy(false);
        return;
      }

      const jobId = startData.job_id;
      setCurrentJobId(jobId);
      setImportResult({ status: 'pending', processed_rows: 0, total_rows: parseResult.rows?.length || 0, imported: 0, duplicates: 0, errors: 0 });

      addJob({
        job_id: jobId,
        label: `Patient Import (${parseResult.rows?.length || 0} rows)`,
        batch_session: parseResult.batch_session,
        status: 'pending',
        total_rows: parseResult.rows?.length || 0,
        processed_rows: 0,
        imported: 0, duplicates: 0, errors: 0,
        poll_url: `/api/patients/bulk-import/jobs/${jobId}`,
      });

      _startPolling(jobId);

    } catch {
      setImportResult({ _failed: true, _reason: 'Network error starting import', _status: 0 });
      setStep('import_failed');
      toast.error('Network error starting import');
      setBusy(false);
    }
  };

  const handleRetryImport = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    handleImport();
  };

  const handleMinimize = () => {
    if (currentJobId) {
      onMinimize?.(currentJobId);
    }
  };

  const handleRollback = async () => {
    if (!parseResult?.batch_session) return;
    setStep('rolling_back');
    setBusy(true);
    try {
      const res = await authFetch(`${API}/api/patients/bulk-upload/${parseResult.batch_session}/rollback`, {
        method: 'DELETE',
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Rollback complete: ${data.deleted || 0} patient${data.deleted !== 1 ? 's' : ''} removed`);
        onSuccess?.();
        onOpenChange(false);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Rollback failed');
        setStep('done');
      }
    } catch {
      toast.error('Network error during rollback');
      setStep('done');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="upload-patients-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileUp className="h-5 w-5" /> Upload Patient Data</DialogTitle>
          <DialogDescription>Import patients from a CSV, XLS, XLSX, or TXT file</DialogDescription>
        </DialogHeader>

        {/* Step: Pick file */}
        {step === 'pick' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center hover:border-slate-400 transition-colors cursor-pointer"
              onClick={() => fileRef.current?.click()} data-testid="upload-dropzone">
              <FileText className="h-8 w-8 text-slate-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-700">Click to select a file</p>
              <p className="text-xs text-slate-500 mt-1">Supported: CSV, XLS, XLSX, TXT</p>
              <p className="text-[10px] text-slate-400 mt-2">Required columns: first_name, last_name, date_of_birth</p>
            </div>
            <input ref={fileRef} type="file" className="hidden" accept=".csv,.xls,.xlsx,.txt" onChange={handleFilePick} data-testid="upload-file-input" />
          </div>
        )}

        {/* Step: Parsing */}
        {step === 'parsing' && (
          <div className="py-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-3" />
            <p className="text-sm text-slate-600">Parsing {file?.name}...</p>
          </div>
        )}

        {/* Step: Mapping */}
        {step === 'mapping' && parseResult && (
          <div className="space-y-4" data-testid="upload-mapping">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Map your file columns to patient fields.
                <span className="text-slate-400 ml-1">({parseResult.source_headers?.length || 0} columns detected)</span>
              </p>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={handleAutoMap} data-testid="auto-map-btn">
                  <CheckCircle2 className="h-3 w-3" /> Auto-map
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={handleResetMapping} data-testid="reset-mapping-btn">
                  <X className="h-3 w-3" /> Reset
                </Button>
              </div>
            </div>

            {/* First 3 rows mini-preview */}
            {parseResult.raw_rows?.length > 0 && (
              <div className="border rounded-lg overflow-hidden" data-testid="mapping-preview-panel">
                <div className="bg-slate-50 px-3 py-1.5 border-b border-slate-200">
                  <p className="text-[10px] font-medium text-slate-500">First {Math.min(3, parseResult.raw_rows.length)} rows preview</p>
                </div>
                <div className="overflow-x-auto max-h-[100px]">
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="border-b border-slate-100">
                        {(parseResult.source_headers || []).map(h => (
                          <th key={h} className="px-2 py-1 text-left text-slate-500 font-medium whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parseResult.raw_rows.slice(0, 3).map((row, ri) => (
                        <tr key={ri} className={ri % 2 === 0 ? '' : 'bg-slate-50/50'}>
                          {(parseResult.source_headers || []).map(h => (
                            <td key={h} className="px-2 py-0.5 text-slate-600 whitespace-nowrap max-w-[120px] truncate">{row[h] || ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="border rounded-lg overflow-hidden max-h-[320px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs w-[30%]">Source Column</TableHead>
                    <TableHead className="text-xs w-[30%]">Sample Data</TableHead>
                    <TableHead className="text-xs w-[40%]">Map To</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(parseResult.source_headers || []).map((header) => {
                    const currentTarget = columnMapping[header] || '';
                    const usedTargets = getUsedTargets();
                    const samples = _getSamples(parseResult.raw_rows, header);
                    const colValues = (parseResult.raw_rows || []).map(r => r[header] || '');
                    const hint = _guessColumnType(colValues);
                    return (
                      <TableRow key={header}>
                        <TableCell className="py-2" data-testid={`mapping-source-${header}`}>
                          <span className="text-xs font-mono text-slate-800">{header}</span>
                          {hint?.type === 'fullname' && (
                            <p className="text-[9px] text-amber-600 mt-0.5" data-testid={`composite-hint-${header}`}>Detected possible composite name</p>
                          )}
                        </TableCell>
                        <TableCell className="py-2" data-testid={`mapping-sample-${header}`}>
                          {samples.length > 0 ? (
                            <div className="space-y-0.5">
                              <p className="text-[11px] text-slate-600 truncate max-w-[140px]" title={samples[0]}>{samples[0]}</p>
                              {samples[1] && <p className="text-[10px] text-slate-400 truncate max-w-[140px]" title={samples[1]}>{samples[1]}</p>}
                              {hint && (
                                <span className={cn("inline-block text-[8px] px-1.5 py-0 rounded-full border", HINT_COLORS[hint.type] || 'text-slate-500 bg-slate-50 border-slate-200')}
                                  data-testid={`mapping-hint-${header}`}>{hint.label}</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[10px] text-slate-300 italic">empty</span>
                          )}
                        </TableCell>
                        <TableCell className="py-1.5">
                          <Select
                            value={currentTarget || 'skip'}
                            onValueChange={(val) => handleMappingChange(header, val === 'skip' ? '' : val)}
                          >
                            <SelectTrigger
                              className={cn("h-8 text-xs", currentTarget ? "border-green-300 bg-green-50" : "border-slate-200")}
                              data-testid={`mapping-target-${header}`}
                            >
                              <SelectValue placeholder="Skip this column" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="skip" className="text-xs text-slate-400">— Skip —</SelectItem>
                              {effectiveFields.map(tf => {
                                const isUsedByOther = usedTargets.has(tf.value) && currentTarget !== tf.value;
                                return (
                                  <SelectItem
                                    key={tf.value}
                                    value={tf.value}
                                    className={cn("text-xs", isUsedByOther && "opacity-40")}
                                    disabled={isUsedByOther}
                                  >
                                    {tf.label}{tf.required ? ' *' : ''}{isUsedByOther ? ' (in use)' : ''}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Required fields status */}
            <div className="flex flex-wrap gap-1.5">
              {effectiveFields.filter(f => f.required).map(f => {
                const isMapped = Object.values(columnMapping).includes(f.value) ||
                  (compositeSplit?.source && (f.value === 'first_name' || f.value === 'last_name'));
                return (
                  <Badge key={f.value} variant="outline"
                    className={cn("text-[10px] px-1.5 py-0", isMapped ? "text-green-700 border-green-300 bg-green-50" : "text-red-600 border-red-200 bg-red-50")}
                    data-testid={`required-badge-${f.value}`}
                  >
                    {isMapped ? <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> : <AlertCircle className="h-2.5 w-2.5 mr-0.5" />}
                    {f.label}
                  </Badge>
                );
              })}
            </div>

            {/* Composite name split */}
            {(parseResult.composite_columns?.length > 0 || parseResult.source_headers?.length > 0) && (
              <div className="border rounded-lg p-3 bg-slate-50 space-y-2" data-testid="composite-split-section">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-slate-700">Composite Name Split</p>
                    <p className="text-[10px] text-slate-500">Split a single name column into first/last name</p>
                  </div>
                  {compositeSplit ? (
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-500"
                      onClick={() => setCompositeSplit(null)} data-testid="composite-clear-btn">Clear</Button>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <Select value={compositeSplit?.source || ''} onValueChange={src => setCompositeSplit(prev => ({ ...prev, source: src, pattern: prev?.pattern || 'LastName, FirstName' }))}>
                    <SelectTrigger className="h-7 text-xs flex-1" data-testid="composite-source">
                      <SelectValue placeholder="Select column..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(parseResult.composite_columns || parseResult.source_headers || []).map(h => (
                        <SelectItem key={h} value={h} className="text-xs">{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={compositeSplit?.pattern || 'LastName, FirstName'} onValueChange={p => setCompositeSplit(prev => ({ ...prev, pattern: p }))}>
                    <SelectTrigger className="h-7 text-xs flex-1" data-testid="composite-pattern">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPOSITE_PATTERNS.map(cp => (
                        <SelectItem key={cp.value} value={cp.value} className="text-xs">{cp.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Validation errors */}
            {mappingErrors.length > 0 && (
              <div className="border border-red-200 rounded-lg bg-red-50 p-2.5" data-testid="mapping-validation-errors">
                {mappingErrors.map((err, i) => (
                  <p key={i} className="text-[11px] text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3 flex-shrink-0" /> {err}
                  </p>
                ))}
              </div>
            )}

            <DialogFooter className="flex items-center justify-between">
              <Button variant="outline" onClick={() => { setStep('pick'); setFile(null); }} data-testid="mapping-back-btn">Back</Button>
              <div className="flex items-center gap-2">
                {USE_VENDOR_FLOW && (
                  <span className="text-[9px] text-slate-400">Parse &rarr; Map &rarr; Apply &rarr; Import</span>
                )}
                <Button onClick={applyMappingAndProceed} disabled={busy}
                  className="bg-black hover:bg-gray-800 gap-2" data-testid="mapping-continue-btn">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  {USE_VENDOR_FLOW ? 'Apply Mapping' : 'Continue to Preview'} <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </DialogFooter>
          </div>
        )}

        {/* Step: Applying */}
        {step === 'applying' && (
          <div className="py-12 text-center space-y-3" data-testid="upload-applying">
            <Loader2 className="h-8 w-8 text-slate-400 animate-spin mx-auto" />
            <p className="text-sm font-medium text-slate-700">Applying column mapping...</p>
            <p className="text-xs text-slate-500">Transforming rows using your mapping configuration</p>
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && parseResult && (
          <div className="space-y-4" data-testid="upload-preview">
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="font-medium">{parseResult.valid_rows} valid</span>
              </div>
              {parseResult.error_rows > 0 && (
                <div className="flex items-center gap-1.5">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  <span className="font-medium">{parseResult.error_rows} error{parseResult.error_rows > 1 ? 's' : ''}</span>
                </div>
              )}
              <span className="text-slate-500">{parseResult.total_rows} total rows</span>
            </div>
            {parseResult.rows.length > 0 && (
              <div className="border rounded-lg overflow-hidden max-h-[200px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Name</TableHead><TableHead>DOB</TableHead><TableHead>Gender</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {parseResult.rows.slice(0, 10).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs">{r.first_name} {r.last_name}</TableCell>
                        <TableCell className="text-xs">{r.date_of_birth}</TableCell>
                        <TableCell className="text-xs">{r.gender || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {parseResult.rows.length > 10 && <p className="text-xs text-slate-500 p-2 text-center">+ {parseResult.rows.length - 10} more</p>}
              </div>
            )}
            {parseResult.errors.length > 0 && (
              <div className="border border-amber-200 rounded-lg bg-amber-50 p-3 max-h-[120px] overflow-y-auto">
                <p className="text-xs font-medium text-amber-700 mb-1">Rows with errors (will be skipped):</p>
                {parseResult.errors.slice(0, 5).map((e, i) => (
                  <p key={i} className="text-[10px] text-amber-600">Row {e.row}: {e.error}</p>
                ))}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('mapping')} data-testid="preview-back-btn">Back</Button>
              <Button onClick={handleImport} disabled={parseResult.valid_rows === 0 || busy}
                className="bg-black hover:bg-gray-800 gap-2" data-testid="confirm-import-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Import {parseResult.valid_rows} Patient{parseResult.valid_rows > 1 ? 's' : ''}</>}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: Importing */}
        {step === 'importing' && (
          <div className="py-6 space-y-4" data-testid="import-progress">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-700">
                {importResult?.status === 'processing' ? 'Importing patients...' : 'Starting import...'}
              </p>
            </div>
            {importResult && typeof importResult.processed_rows === 'number' && importResult.total_rows > 0 && (
              <>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div className="bg-black rounded-full h-2 transition-all duration-500"
                    style={{ width: `${Math.round((importResult.processed_rows / importResult.total_rows) * 100)}%` }}
                    data-testid="import-progress-bar" />
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span data-testid="import-progress-text">{importResult.processed_rows} / {importResult.total_rows} rows</span>
                  <span>{Math.round((importResult.processed_rows / importResult.total_rows) * 100)}%</span>
                </div>
                <div className="flex items-center justify-center gap-4 text-xs">
                  {importResult.imported > 0 && <span className="text-green-700">{importResult.imported} imported</span>}
                  {importResult.duplicates > 0 && <span className="text-amber-600">{importResult.duplicates} duplicates</span>}
                  {importResult.errors > 0 && <span className="text-red-600">{importResult.errors} errors</span>}
                </div>
              </>
            )}
            <DialogFooter>
              <Button variant="outline" className="gap-1.5" onClick={handleMinimize} data-testid="minimize-import-btn">
                <Minimize2 className="h-3.5 w-3.5" /> Minimize
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: Import Failed */}
        {step === 'import_failed' && (
          <div className="space-y-4" data-testid="import-failed">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
              <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
              <p className="text-sm font-semibold text-red-800">Import Failed</p>
              <p className="text-xs text-red-600 mt-1">
                {importResult?._reason || 'The import could not complete. Your data is safe — you can retry.'}
              </p>
              {importResult?._status ? (
                <p className="text-[10px] text-red-400 mt-1">Status: {importResult._status}</p>
              ) : null}
            </div>
            <p className="text-xs text-slate-500 text-center">
              The server may have processed some rows. Retrying will resume from where it left off.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('preview')} data-testid="import-failed-back-btn">Back to Preview</Button>
              <Button onClick={handleRetryImport} disabled={busy}
                className="bg-black hover:bg-gray-800 gap-2" data-testid="retry-import-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                Retry Import
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: Done */}
        {step === 'done' && importResult && (
          <div className="space-y-4" data-testid="upload-result">
            <div className={cn("rounded-lg border p-4 text-center",
              importResult.imported > 0 ? "bg-green-50 border-green-200" : importResult.duplicates > 0 ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"
            )}>
              {importResult.imported > 0
                ? <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
                : importResult.duplicates > 0
                  ? <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                  : <CheckCircle2 className="h-8 w-8 text-slate-400 mx-auto mb-2" />
              }
              <p className={cn("text-sm font-semibold",
                importResult.imported > 0 ? "text-green-800" : importResult.duplicates > 0 ? "text-amber-800" : "text-slate-700"
              )}>
                {importResult.imported > 0 ? 'Import Complete' : importResult.duplicates > 0 ? 'All Records Already Exist' : 'Import Complete'}
              </p>
            </div>
            <div className="flex items-center justify-center gap-6 text-sm">
              <div className="text-center" data-testid="import-count-imported"><p className="font-bold text-green-700">{importResult.imported}</p><p className="text-xs text-slate-500">Imported</p></div>
              <div className="text-center" data-testid="import-count-duplicates"><p className="font-bold text-amber-600">{importResult.duplicates}</p><p className="text-xs text-slate-500">Duplicates</p></div>
              <div className="text-center" data-testid="import-count-errors"><p className="font-bold text-red-600">{importResult.errors}</p><p className="text-xs text-slate-500">Errors</p></div>
            </div>
            {importResult.duplicates > 0 && importResult.details && (
              <div className="border border-amber-200 rounded-lg bg-amber-50 p-3 max-h-[120px] overflow-y-auto" data-testid="duplicate-details">
                <p className="text-xs font-medium text-amber-700 mb-1">Duplicate records (skipped):</p>
                {importResult.details.filter(d => d.status === 'duplicate').slice(0, 8).map((d, idx) => (
                  <p key={idx} className="text-[10px] text-amber-600">
                    Row {d.row}: {d.name || 'Unknown'}{d.reason ? ` — ${d.reason}` : ''}
                  </p>
                ))}
                {importResult.details.filter(d => d.status === 'duplicate').length > 8 && (
                  <p className="text-[10px] text-amber-500 mt-1">
                    + {importResult.details.filter(d => d.status === 'duplicate').length - 8} more
                  </p>
                )}
              </div>
            )}
            {importResult.errors > 0 && importResult.details && (
              <div className="border border-red-200 rounded-lg bg-red-50 p-3 max-h-[120px] overflow-y-auto" data-testid="error-details">
                <p className="text-xs font-medium text-red-700 mb-1">Failed rows:</p>
                {importResult.details.filter(d => d.status === 'error').slice(0, 8).map((d, idx) => (
                  <p key={idx} className="text-[10px] text-red-600">
                    Row {d.row}: {d.error || 'Unknown error'}
                  </p>
                ))}
                {importResult.details.filter(d => d.status === 'error').length > 8 && (
                  <p className="text-[10px] text-red-500 mt-1">
                    + {importResult.details.filter(d => d.status === 'error').length - 8} more
                  </p>
                )}
              </div>
            )}
            {importResult.resumed && (
              <p className="text-[10px] text-slate-400 text-center" data-testid="resumed-badge">
                Resumed from prior attempt (processed {importResult.processed_rows}/{importResult.processed_rows + importResult.remaining_rows})
              </p>
            )}
            {parseResult?.batch_session && (
              <div className="flex items-center justify-center gap-1.5" data-testid="batch-session-info">
                <span className="text-[10px] text-slate-400">Batch:</span>
                <code className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded font-mono select-all">{parseResult.batch_session}</code>
              </div>
            )}
            <DialogFooter className="flex items-center justify-between">
              {importResult.remaining_rows > 0 && (
                <Button variant="outline" className="text-xs gap-1"
                  onClick={handleRetryImport} disabled={busy} data-testid="retry-remaining-btn">
                  <RotateCcw className="h-3 w-3" /> Retry Remaining ({importResult.remaining_rows})
                </Button>
              )}
              {importResult.imported > 0 && parseResult?.batch_session && (
                <Button variant="outline" className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                  onClick={handleRollback} disabled={busy} data-testid="rollback-import-btn">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <X className="h-3.5 w-3.5 mr-1" />}
                  Undo Import
                </Button>
              )}
              <Button onClick={() => onOpenChange(false)} className="bg-black hover:bg-gray-800" data-testid="upload-done-btn">Done</Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: Rolling back */}
        {step === 'rolling_back' && (
          <div className="py-8 text-center" data-testid="upload-rolling-back">
            <Loader2 className="h-8 w-8 animate-spin text-red-400 mx-auto mb-3" />
            <p className="text-sm text-slate-600">Rolling back imported patients...</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UploadPatientsModal;
